import {
  Box, Button, Chip, CircularProgress, Container, Grid, Paper, Typography,
  Stepper, Step, StepLabel, StepContent, Alert, LinearProgress,
} from "@mui/material";
import {
  ChevronLeft, RocketLaunch,
  CheckCircle, Add,
  Visibility,
} from "@mui/icons-material";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import RichContentViewer from "../../../Shared/RichContentViewer";
import ProjectActivity from "./ProjectActivity";
import ProjectFinancials from "./ProjectFinancials";
import ProjectSidebar from "./ProjectSidebar";
import CorpusDisbursementForm from "./CorpusDisbursementForm";
import { ProjectContext } from "../../../AppContext/ProjectContext";
import { PayContext } from "../../../AppContext/PayContext";
import { useProjectRetrieves } from "../../../Config/db/project.retrieve";
import { AuthContext } from "../../../AppContext/AuthContext";
import { AccountContext } from "../../../AppContext/AccountContext";
import ConsolidatedActions from "../Actions/ConsolidatedActions";
import { STATUS_CONFIG, PROJECT_TYPE_META } from "../../constants";
import StatusChangeDialog from "./StatusChangeDialog";
import useJourneySteps from "./useJourneySteps";
import { savePayProjectGroup } from "../../../Config/db/paylinks.operations";
import { getCorpusDisbursementsInRealTime } from "../../../Config/db/corpus-disbursement.operations";
import { doc, updateDoc, arrayUnion } from "firebase/firestore";
import { db } from "../../../Config/firebase";
import { GatedAction } from "../../../Shared/AreaGate";
import ACTION_IDS from "../../../Shared/actionIds";
import { logProjectActivity } from "../../../Config/db/project-activity.operations";

const ViewProject = () => {
  const { projectNumber } = useParams();
  const { projectInfo, estimatedToResolve, participants, refreshProjects, allProjects, actions, setProjectInfo } =
    useContext(ProjectContext);
  const { userInfo, totalFlats } = useContext(AuthContext);
  const { bills, revenueBills } = useContext(PayContext);
  const { primaryAccount, virtualAccounts } = useContext(AccountContext);
  const { getProjectByProjectNumber, updateProject } = useProjectRetrieves();
  const navigate = useNavigate();
  const [corpusFormOpen, setCorpusFormOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);

  const handleStatusConfirm = ({ newStatus, comment, timestamp }) => {
    const historyEntry = {
      from: projectInfo.status || "open",
      to: newStatus,
      comment: comment || "",
      timestamp,
      user: userInfo?.name || userInfo?.email || "Unknown",
    };
    updateProject({
      status: newStatus,
      statusHistory: arrayUnion(historyEntry),
    });
    logProjectActivity({
      projectId: projectInfo.id,
      communityCode: userInfo?.communityCode,
      userId: userInfo?.uid,
      userName: userInfo?.name || userInfo?.email,
      type: "status_change",
      description: `Status changed from "${projectInfo.status || "open"}" to "${newStatus}"${comment ? ` — ${comment}` : ""}`,
      meta: { from: projectInfo.status || "open", to: newStatus },
    });
    refreshProjects();
    setStatusDialogOpen(false);
  };

  const handleClassify = (type) => {
    updateProject({ projectType: type });
    const meta = PROJECT_TYPE_META[type] || {};
    logProjectActivity({
      projectId: projectInfo.id,
      communityCode: userInfo?.communityCode,
      userId: userInfo?.uid,
      userName: userInfo?.name || userInfo?.email,
      type: "classified",
      description: `Project classified as "${meta.label || type}"`,
      meta: { projectType: type },
    });
    refreshProjects();
  };

  useEffect(() => {
    if (userInfo?.communityCode) {
      // Always re-fetch when URL projectNumber doesn't match loaded projectInfo
      if (!projectInfo || String(projectInfo.projectNumber) !== String(projectNumber)) {
        setProjectInfo(null); // clear stale data & subscriptions immediately
        getProjectByProjectNumber(projectNumber, userInfo.communityCode);
      }
    }
  }, [projectNumber, userInfo?.communityCode]);

  /* ── corpus / fund-raise disbursements (real-time) ── */
  const [corpusDisbursements, setCorpusDisbursements] = useState([]);
  useEffect(() => {
    if (projectInfo?.id && (projectInfo?.projectType === "corpus" || projectInfo?.projectType === "fund-raise")) {
      const unsub = getCorpusDisbursementsInRealTime(projectInfo.id, (data) => {
        setCorpusDisbursements(data);
      });
      return () => unsub?.();
    } else {
      setCorpusDisbursements([]);
    }
  }, [projectInfo?.id, projectInfo?.projectType]);

  /* ── linked financial data ───────────────────────── */
  const linkedPayComponents = useMemo(() => {
    if (!projectInfo?.id || !virtualAccounts?.length) return [];
    return virtualAccounts.filter((va) => {
      const linked = va.linkedProjects || [];
      return linked.some?.((lt) => lt.value === projectInfo.id || lt === projectInfo.id);
    });
  }, [virtualAccounts, projectInfo]);

  // All bills (expense + revenue) linked to this project's pay components
  const allLinkedBills = useMemo(() => {
    if (!linkedPayComponents.length) return [];
    const vaIds = new Set(linkedPayComponents.map((va) => va.id));
    const allBills = [...(bills || []), ...(revenueBills || [])];
    const seen = new Set();
    return allBills.filter((b) => {
      const matchId = b.accountHeadId;
      if (!vaIds.has(matchId)) return false;
      if (b.id && seen.has(b.id)) return false;
      if (b.id) seen.add(b.id);
      return true;
    });
  }, [linkedPayComponents, bills, revenueBills]);

  // Dues = linked bills that are unpaid (transactionId is empty)
  const linkedBills = useMemo(() => {
    return allLinkedBills.filter((b) => !b.transactionId);
  }, [allLinkedBills]);

  // Collections = linked bills that are paid (transactionId is non-empty)
  const linkedPayments = useMemo(() => {
    return allLinkedBills.filter((b) => !!b.transactionId);
  }, [allLinkedBills]);

  /* ── available (unlinked) expense items for fund-raise ── */
  const availableItems = useMemo(() => {
    if (!projectInfo?.id || projectInfo?.projectType !== "fund-raise") return [];
    const linkedIds = new Set(linkedPayComponents.map((va) => va.id));
    return virtualAccounts.filter((va) => {
      if (linkedIds.has(va.id)) return false;
      const existingLinks = va.linkedProjects || [];
      if (existingLinks.length > 0) return false;
      return true;
    });
  }, [virtualAccounts, projectInfo, linkedPayComponents]);

  /* ── link an existing pay component to this project ─ */
  const handleLinkPayComponent = async (va) => {
    if (!projectInfo?.id || !userInfo?.communityCode) return;
    const projectOption = { label: `${projectInfo.projectNumber} ${projectInfo.title}`, value: projectInfo.id };
    try {
      await savePayProjectGroup(
        `${va.id}${projectInfo.id}`,
        userInfo.communityCode,
        { payId: va.id, projectId: projectInfo.id }
      );
      await updateDoc(doc(db, "accounts", va.id), {
        linkedProjects: arrayUnion(projectOption),
      });
      refreshProjects();
    } catch (err) {
      console.error("Failed to link account head:", err);
    }
  };

  /* ── journey step calculation ────────────────────── */
  const journeySteps = useJourneySteps({
    projectInfo: projectInfo,
    linkedPayComponents,
    linkedBills,
    linkedPayments,
    corpusDisbursements,
    primaryAccount,
    allProjects,
    actions,
  });

  if (!projectNumber) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: "center" }}>
        <RocketLaunch sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          Nothing to show
        </Typography>
      </Container>
    );
  }

  if (!projectInfo) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: "center" }}>
        <CircularProgress sx={{ color: "#2e7d32" }} />
      </Container>
    );
  }

  const objMeta = PROJECT_TYPE_META[projectInfo.projectType];

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* ── Gradient Header ─────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: { xs: 2, sm: 3 },
          borderRadius: 3,
          background: "linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)",
          color: "#fff",
        }}
      >
        <Box sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" },
          gap: 1,
          mb: 2,
        }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ChevronLeft />}
            sx={{ color: "#fff", borderColor: "rgba(255,255,255,.4)", minWidth: "auto" }}
            onClick={() => navigate("/works")}
          >
            Back
          </Button>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            {objMeta && (
              <Chip
                icon={React.createElement(objMeta.icon, { sx: { color: "white !important", fontSize: 16 } })}
                label={objMeta.label}
                size="small"
                sx={{ bgcolor: objMeta.color, color: "#fff", fontWeight: 600 }}
              />
            )}
            <Chip
              label={`#${projectInfo.projectNumber}`}
              size="small"
              sx={{ bgcolor: "rgba(255,255,255,.2)", color: "#fff", fontWeight: 700, fontSize: 14 }}
            />
          </Box>
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: "1.25rem", sm: "1.5rem" } }}>
          {projectInfo.title}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
          {projectInfo.status && (
            <Chip
              label={STATUS_CONFIG[projectInfo.status]?.label || projectInfo.status}
              size="small"
              color={STATUS_CONFIG[projectInfo.status]?.color || "default"}
              onClick={() => setStatusDialogOpen(true)}
              sx={{ cursor: "pointer" }}
            />
          )}
          <StatusChangeDialog
            open={statusDialogOpen}
            currentStatus={projectInfo.status || "open"}
            onClose={() => setStatusDialogOpen(false)}
            onConfirm={handleStatusConfirm}
          />
        </Box>
      </Paper>

      {/* ── Journey Progress ────────────────────────── */}
      <Paper
        elevation={0}
        sx={{ p: { xs: 1.5, sm: 3 }, mb: { xs: 2, sm: 3 }, borderRadius: 3, border: "1px solid #e0e0e0" }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <RocketLaunch color="primary" />
          <Typography variant="subtitle1" fontWeight={700}>
            Project Journey
          </Typography>
        </Box>

        {!projectInfo.projectType && (
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            Classify this project to unlock the next steps in the journey.
          </Alert>
        )}

        <Stepper
          activeStep={-1}
          orientation="vertical"
          sx={{ "& .MuiStepConnector-line": { minHeight: 20 } }}
        >
          {journeySteps.map((step, idx) => {
            /* Each step is independently colored based on its own state */
            const hasActivity = step.progress > 0 || step.count > 0 || step.done;
            const iconColor = step.done
              ? "#43a047"
              : hasActivity
              ? "#1976d2"
              : "#bdbdbd";
            return (
            <Step key={step.label} completed={step.done} expanded>
              <StepLabel
                StepIconProps={{
                  sx: { color: `${iconColor} !important` },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography fontWeight={600} sx={{ fontSize: "0.95rem" }}>
                    {step.label}
                  </Typography>
                  {step.done && <CheckCircle sx={{ fontSize: 16, color: "#43a047" }} />}
                </Box>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {step.description}
                </Typography>

                {/* Progress bar for steps with percentage tracking */}
                {step.progress !== undefined && (
                  <Box sx={{ mb: 1.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                      <Box sx={{ flex: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(step.progress, 100)}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            bgcolor: "#e0e0e0",
                            "& .MuiLinearProgress-bar": {
                              borderRadius: 4,
                              bgcolor: step.overBudget
                                ? "#d32f2f"
                                : step.progress >= 100
                                ? "#43a047"
                                : "#1976d2",
                            },
                          }}
                        />
                      </Box>
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        sx={{ minWidth: 40, textAlign: "right", color: step.overBudget ? "#d32f2f" : "text.primary" }}
                      >
                        {step.progress}%
                      </Typography>
                    </Box>
                    {step.overBudget && (
                      <Chip
                        label="Over Budget!"
                        size="small"
                        color="error"
                        variant="outlined"
                        sx={{ fontWeight: 600, fontSize: "0.7rem", height: 20 }}
                      />
                    )}

                    {/* Recurring: overall progress bar + cycle count */}
                    {step.recurring && step.overallProgress !== undefined && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: "block" }}>
                          Overall ({step.completedCycles}/{step.totalCycles} cycles complete)
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{ flex: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(step.overallProgress, 100)}
                              sx={{
                                height: 6,
                                borderRadius: 3,
                                bgcolor: "#e0e0e0",
                                "& .MuiLinearProgress-bar": {
                                  borderRadius: 3,
                                  bgcolor: step.overallProgress >= 100 ? "#43a047" : "#7b1fa2",
                                },
                              }}
                            />
                          </Box>
                          <Typography variant="caption" fontWeight={600} sx={{ minWidth: 40, textAlign: "right" }}>
                            {step.overallProgress}%
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Classify action */}
                {step.action === "classify" && (
                  <Box>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
                      {Object.entries(PROJECT_TYPE_META).map(([key, meta]) => (
                        <Button
                          key={key}
                          variant="outlined"
                          size="small"
                          startIcon={React.createElement(meta.icon)}
                          onClick={() => handleClassify(key)}
                          sx={{
                            borderColor: meta.color,
                            color: meta.color,
                            textTransform: "none",
                            fontWeight: 600,
                            "&:hover": { bgcolor: `${meta.color}12` },
                          }}
                        >
                          {meta.label}
                        </Button>
                      ))}
                    </Box>
                    {step.corpusHint && (
                      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                        <Chip
                          size="small"
                          variant="outlined"
                          color="secondary"
                          label={`Corpus balance: ₹${step.corpusHint.totalCorpusBalance.toLocaleString()}`}
                        />
                        {step.corpusHint.reservedCorpus > 0 && (
                          <Chip
                            size="small"
                            variant="outlined"
                            color="warning"
                            label={`Reserved by other projects: ₹${step.corpusHint.reservedCorpus.toLocaleString()}`}
                          />
                        )}
                        <Chip
                          size="small"
                          variant="outlined"
                          color={step.corpusHint.availableCorpus >= (Number(projectInfo.estimatedBudget) || 0) ? "success" : "error"}
                          label={`Available: ₹${step.corpusHint.availableCorpus.toLocaleString()}`}
                        />
                      </Box>
                    )}
                  </Box>
                )}

                {/* Setup settlement config action */}
                {step.action === "setup-component" && (
                  <Box sx={{ mt: 0.5 }}>
                    {/* Existing unlinked expense items */}
                    {availableItems.length > 0 && (
                      <Box sx={{ mb: 1.5 }}>
                        <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
                          Link existing expense items:
                        </Typography>
                        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                          {availableItems.map((pc) => (
                            <Chip
                              key={pc.id}
                              label={pc.state?.title || pc.title || "Untitled"}
                              size="small"
                              variant="outlined"
                              color="warning"
                              icon={<Add sx={{ fontSize: 16 }} />}
                              onClick={() => handleLinkPayComponent(pc)}
                              sx={{
                                cursor: "pointer",
                                fontWeight: 500,
                                "&:hover": { bgcolor: "warning.50" },
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {linkedPayComponents.length > 0 && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Visibility />}
                          onClick={() =>
                            navigate("/fund-raise-details", {
                              state: linkedPayComponents[0],
                            })
                          }
                          sx={{ textTransform: "none", fontWeight: 600 }}
                        >
                          View Settlement Config
                        </Button>
                      )}
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<Add />}
                        onClick={() =>
                          navigate("/account-form", {
                            state: {
                              purpose: projectInfo.title,
                              direction: "credit",
                              billingType: "per-flat",
                              amount: projectInfo.estimatedBudget && totalFlats > 0
                                ? String(Math.round(Number(projectInfo.estimatedBudget) / totalFlats))
                                : "",
                              frequency: "one-time",
                              applicability: "same",
                              linkedProjects: [{ label: `${projectInfo.projectNumber} ${projectInfo.title}`, value: projectInfo.id }],
                              returnTo: `/project/${projectInfo.projectNumber}`,
                            },
                          })
                        }
                        sx={{ textTransform: "none", fontWeight: 600 }}
                      >
                        Configure Settlement
                      </Button>
                    </Box>
                  </Box>
                )}

                {/* Corpus debit action */}
                {step.action === "corpus-debit" && (
                  <GatedAction actionId={ACTION_IDS.FINANCE_RECORD_CHEQUE_DEBIT}>
  

                  <Box sx={{ mt: 0.5 }}>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Add />}
                      onClick={() => setCorpusFormOpen(true)}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        background: "linear-gradient(135deg, #6a1b9a 0%, #7b1fa2 100%)",
                      }}
                    >
                      Record Cheque / Debit
                    </Button>
                  </Box>
                  </GatedAction>
                )}

                {/* Track progress slider */}
              </StepContent>
            </Step>
            );
          })}
        </Stepper>

        {/* Corpus Disbursement Form Dialog */}
        <CorpusDisbursementForm
          open={corpusFormOpen}
          handleClose={() => setCorpusFormOpen(false)}
          existingDisbursements={corpusDisbursements}
        />
      </Paper>

      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {/* ── Main Content ───────────────────────────── */}
        <Grid item xs={12} md={9}>
          <Paper
            elevation={0}
            sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 }, borderRadius: 3, border: "1px solid #e0e0e0" }}
          >
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Description
            </Typography>
            <Box sx={{ overflow: "hidden", wordBreak: "break-word" }}>
              <RichContentViewer content={projectInfo.description} />
            </Box>
          </Paper>

          <ProjectFinancials
            projectInfo={projectInfo}
            corpusDisbursements={corpusDisbursements}
            linkedBills={linkedBills}
            linkedPayments={linkedPayments}
          />

          <Paper
            elevation={0}
            sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 }, borderRadius: 3, border: "1px solid #e0e0e0" }}
          >
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Tasks
            </Typography>
            <ConsolidatedActions />
          </Paper>

          <ProjectActivity corpusDisbursements={corpusDisbursements} />
        </Grid>

        {/* ── Sidebar ─────────────────────────────────── */}
        <Grid item xs={12} md={3}>
          <ProjectSidebar
            projectInfo={projectInfo}
            actions={actions}
            participants={participants}
            estimatedToResolve={estimatedToResolve}
            objMeta={objMeta}
            onStatusClick={() => setStatusDialogOpen(true)}
          />
        </Grid>
      </Grid>
    </Container>
  );
};

export default ViewProject;
